'use client';

import { useEffect, useMemo, useState } from 'react';
import FundingPageChart from '../FundingPageChart';
import GridSectionWrapper from '../GridSections/GridSectionWrapper/GridSectionWrapper';
import ExportHistoryButton from 'src/components/Buttons/ExportHistoryButton';
import { AssetFilterWithSearch } from '../Inputs/AssetFilterWithSearch';
import Button from 'src/components/primitives/Button/Button';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import { ToggleTab } from 'src/components/primitives/ToggleTabs/types';
import { SimpleFundingRateRecord } from 'src/@types/types';
import { findDaysOrHoursAgo } from 'src/utils/dateTime';
import PaginatedTableV2 from '../PaginatedTableV2';
import TableV2 from '../Tables/TableV2';
import NumLib from 'src/utils/NumLib';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cj } from 'src/utils/classNames';
import useMarketFundingHistory from 'src/hooks/useMarketFundingHistory';
import Link from 'src/components/primitives/Link/Link';
import { UIMarket, getPausedOperations } from '@velocity-exchange/common';
import { useSearchParams } from 'next/navigation';
import { usePerpMarkets } from 'src/hooks/perpMarkets/usePerpMarkets';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import useDriftClient from 'src/hooks/useDriftClient';
import InlineLoadingBar from '../InlineLoadingBar/InlineLoadingBar';
import { PerpMarketAccount } from '@velocity-exchange/sdk';
import { AssetListAssetWithMarketIndex } from 'src/@types/assets';
import { Swap } from '@velocity-exchange/icons';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import useHiddenPerpMarkets from 'src/hooks/useHiddenPerpMarkets';

const FUNDING_INTERVAL_TABS: { label: string; value: '1h' | '8h' | 'APR' }[] = [
	{ label: '1H', value: '1h' },
	{ label: '8H', value: '8h' },
	{ label: 'APR', value: 'APR' },
];

const FUNDING_PERIOD_TABS: { label: string; value: '1D' | '7D' | '1M' }[] = [
	{ label: '1D', value: '1D' },
	{ label: '7D', value: '7D' },
	{ label: '1M', value: '1M' },
];

const GRAPH_PERIOD_CONFIG = {
	'1D': { label: '1D', unit: 'hours', ago: 24 },
	'7D': { label: '7D', unit: 'days', ago: 7 },
	'1M': { label: '1M', unit: 'days', ago: 30 },
};

const TIME_MULTIPLIERS = {
	'1h': 1,
	'8h': 8,
	APR: 365.25 * 24,
};

const PAGE_SIZE = 20;

const GRID = '1fr 1fr 1fr';

const FundingTable = (props: { fundingRates: SimpleFundingRateRecord[] }) => {
	//const isMobile = useIsMobileScreenSize();
	const [currentPage, setCurrentPage] = useState(0);
	const [currentFundingHistory, setCurrentFundingHistory] = useState<
		SimpleFundingRateRecord[]
	>([]);
	const { displayDatesAsTimestamp, toggleDateDisplayType } =
		useDateDisplayState();

	const resetState = () => {
		setCurrentPage(0);
		setCurrentFundingHistory([]);
	};

	const executePageChange = (newPage: number) => {
		if (newPage === currentPage) return;

		if (newPage === -1) {
			newPage = Math.round(props.fundingRates.length / PAGE_SIZE) - 1;
		}

		setCurrentFundingHistory(
			props.fundingRates.slice(
				newPage * PAGE_SIZE,
				newPage * PAGE_SIZE + PAGE_SIZE
			)
		);

		setCurrentPage(newPage);
	};

	useEffect(() => {
		resetState();
		setCurrentFundingHistory(props.fundingRates?.slice(0, PAGE_SIZE));
	}, [props.fundingRates]);

	return (
		<PaginatedTableV2
			noBorder
			tableGrid={GRID}
			pageSize={PAGE_SIZE}
			currentPage={currentPage}
			pageChangeCallback={executePageChange}
			totalItems={props.fundingRates.length}
			fillSpace
			paginatorClassName="border-stroke-divider rounded-b-md"
			tableState={{
				records: props.fundingRates,
				emptyStateText: 'No Funding History found',
				id: 'funding_history_table',
				requireAccountCreated: false,
				requireWalletConnect: false,
				className: 'max-sm:pb-0',
			}}
			innerClassName="bg-container-bg"
			tableContentTop={
				<TableV2.HeaderRow
					grid={GRID}
					className="w-full text-2xs"
					lastColumnJustify="start"
				>
					<TableV2.HeaderCell key="funding_header_0">
						Funding Rate
					</TableV2.HeaderCell>
					<TableV2.HeaderCell key="funding_header_1">
						Market Price
					</TableV2.HeaderCell>
					<TableV2.TooltipHeaderCell
						key="funding_header_2"
						className="gap-x-xs max-sm:justify-end"
					>
						<Tooltip content="Time when funding rate was recorded">
							Time
						</Tooltip>
						<IconButton
							size="sm"
							variant="tertiary"
							onClick={toggleDateDisplayType}
							icon={Swap}
						/>
					</TableV2.TooltipHeaderCell>
				</TableV2.HeaderRow>
			}
			tableContentMiddle={
				<div className="flex h-full w-full flex-col sm:h-[40vh]">
					{currentFundingHistory?.map((record, index) => (
						<TableV2.BodyRow
							key={`funding_row_${index}`}
							grid={GRID}
							lastColumnJustify="end"
							isDataRow
						>
							<TableV2.BodyCell
								key={`funding_rate_${index}`}
								className={
									record.fundingRate < 0
										? `text-negative-red`
										: `text-positive-green`
								}
							>
								{record.isAsymmetric ? (
									<div>
										<span
											className={
												(record.fundingRateLong ?? 0) < 0
													? `text-negative-red`
													: `text-positive-green`
											}
										>{`${parseFloat(
											record.fundingRateLong?.toFixed(6) ?? '0'
										)}% Long`}</span>{' '}
										/{' '}
										<span
											className={
												(record.fundingRateShort ?? 0) < 0
													? `text-negative-red`
													: `text-positive-green`
											}
										>{`${parseFloat(
											record.fundingRateShort?.toFixed(6) ?? '0'
										)}% Short`}</span>
									</div>
								) : (
									`${parseFloat(record.fundingRate.toFixed(6))}%`
								)}
							</TableV2.BodyCell>
							<TableV2.BodyCell key={`funding_market_price_${index}`}>
								${NumLib.formatNum.toDisplayPrice(record.marketPrice)}
							</TableV2.BodyCell>
							<TableV2.DateCell
								key={`funding_date_${index}`}
								date={new Date(record.ts)}
								displayAsTimestamp={displayDatesAsTimestamp}
								className="max-sm:justify-end"
								innerClassName="max-sm:text-right"
							/>
						</TableV2.BodyRow>
					))}
				</div>
			}
		/>
	);
};

const DEFAULT_MARKET_OPTIONS: { label: string; value: number }[] = [];

const FundingHistoryContent = () => {
	const isMobile = useIsMobileScreenSize();
	const queryMarketSymbol = useSearchParams()?.get('market');
	const currentPerpMarkets = usePerpMarkets();
	const perpMarketsLookup = usePerpMarketsLookup();
	const driftClient = useDriftClient();
	const hiddenPerpMarkets = useHiddenPerpMarkets();

	const perpMarketOptions = useMemo(() => {
		if (!driftClient) return DEFAULT_MARKET_OPTIONS;

		return currentPerpMarkets
			.filter((mkt) => !mkt.category?.includes('Prediction'))
			.filter((mkt) => !hiddenPerpMarkets.has(mkt.marketIndex))
			.filter((mkt) => {
				const marketAccount = driftClient.getPerpMarketAccount(mkt.marketIndex);
				const pausedOperations = getPausedOperations(
					marketAccount as PerpMarketAccount
				);
				const isFundingPaused = pausedOperations?.includes('Funding');

				return !isFundingPaused;
			})
			.map((mkt) => {
				const marketName = UIMarket.createPerpMarket(mkt.marketIndex).uiSymbols
					.marketDisplaySymbol;
				return {
					label: marketName,
					value: mkt.marketIndex,
				};
			});
	}, [currentPerpMarkets, driftClient, hiddenPerpMarkets]);

	// Transform perpMarketOptions for AssetFilterWithSearch component
	const assetFilterOptions: AssetListAssetWithMarketIndex[] = useMemo(() => {
		return perpMarketOptions.map((option) => ({
			ticker: option.value.toString(),
			displayLabel: option.label,
			marketIndex: option.value,
		}));
	}, [perpMarketOptions]);

	const initialMarketConfig = useMemo(() => {
		return (
			perpMarketsLookup.find((market) => market.symbol === queryMarketSymbol) ??
			perpMarketsLookup[0]
		);
	}, [queryMarketSymbol]);

	const [fundingMarketIndex, setFundingMarketIndex] = useState<number>(
		initialMarketConfig.marketIndex
	);
	const [selectedMarketTicker, setSelectedMarketTicker] = useState<string>(
		initialMarketConfig.marketIndex.toString()
	);
	const [fundingInterval, setFundingInterval] = useState<'1h' | '8h' | 'APR'>(
		'1h'
	);
	const [fundingPeriod, setFundingPeriod] = useState<'1D' | '7D' | '1M'>('1M');

	const { data: fundingRates, loading: fundingRatesLoading } =
		useMarketFundingHistory(fundingMarketIndex);

	const filteredFundingRates = useMemo(() => {
		return [...fundingRates]
			?.sort((trade1, trade2) => trade2.ts - trade1.ts)
			.filter(
				(record) =>
					findDaysOrHoursAgo(
						new Date(record.ts),
						GRAPH_PERIOD_CONFIG[fundingPeriod].unit
					) <= GRAPH_PERIOD_CONFIG[fundingPeriod].ago
			)
			.map((record) => {
				return {
					...record,
					fundingRate: record.fundingRate * TIME_MULTIPLIERS[fundingInterval],
				};
			});
	}, [fundingRates, fundingPeriod, fundingInterval]);

	const averageFundingRate = useMemo(() => {
		if (!filteredFundingRates?.length) return 0;
		return (
			filteredFundingRates.reduce((a, b) => a + b.fundingRate, 0) /
			filteredFundingRates.length
		);
	}, [filteredFundingRates]);

	const displayUiMarket = useMemo(() => {
		return UIMarket.createPerpMarket(fundingMarketIndex);
	}, [fundingMarketIndex]);

	// Transform options for ToggleTabs
	const intervalToggleOptions: ToggleTab<'1h' | '8h' | 'APR'>[] = useMemo(
		() =>
			FUNDING_INTERVAL_TABS.map((tab) => ({
				value: tab.value as '1h' | '8h' | 'APR',
				label: tab.label,
			})),
		[]
	);

	const periodToggleOptions: ToggleTab<'1D' | '7D' | '1M'>[] = useMemo(
		() =>
			FUNDING_PERIOD_TABS.map((tab) => ({
				value: tab.value as '1D' | '7D' | '1M',
				label: tab.label,
			})),
		[]
	);

	const handleAssetFilterChange = (selectedTicker: string) => {
		const marketIndex = parseInt(selectedTicker);
		setFundingMarketIndex(marketIndex);
		setSelectedMarketTicker(selectedTicker);
	};

	return (
		<div className="flex flex-col gap-y-2xl max-sm:mb-2xl max-sm:max-w-screen max-sm:gap-y-md max-sm:px-md">
			<GridSectionWrapper
				className={cj(
					'mt-3 p-2xl max-sm:p-md',
					isMobile && 'hidden-scroll h-min w-full overflow-y-scroll rounded-md'
				)}
			>
				<div className="flex w-full justify-between pb-2 max-sm:flex-col max-sm:gap-y-md">
					<AssetFilterWithSearch
						id="funding-market-selector"
						currentSelection={selectedMarketTicker}
						options={assetFilterOptions}
						onChange={handleAssetFilterChange}
						label="Market"
						className="w-56 max-sm:w-full"
					/>
					<div className="flex items-center gap-2 max-sm:w-full max-sm:gap-x-md">
						<ExportHistoryButton
							type="funding-rates"
							className="max-sm:h-6.5 max-sm:w-1/2"
						/>
						<Button
							as="a"
							href={`/${displayUiMarket.uiSymbols.marketSymbol}`}
							variant="primary"
							size="md"
							className="max-sm:h-6.5 max-sm:w-1/2"
						>
							Trade Now
						</Button>
					</div>
				</div>
				<div
					className={cj(
						'flex w-full justify-between',
						isMobile && 'flex flex-col'
					)}
				>
					<div className={cj('flex', isMobile && 'w-full justify-between')}>
						<div className="flex flex-col gap-1 p-2 pl-0">
							<span className="block typo-body-xs text-text-secondary">
								Interval
							</span>

							<Tabs
								size="sm"
								variant="secondary"
								options={intervalToggleOptions}
								activeTab={fundingInterval}
								onTabChange={setFundingInterval}
							/>
						</div>
						<div className="flex flex-col gap-1 p-2">
							<span className="block typo-body-xs text-text-secondary max-sm:text-right">
								Period
							</span>

							<Tabs
								size="sm"
								variant="secondary"
								options={periodToggleOptions}
								activeTab={fundingPeriod}
								onTabChange={setFundingPeriod}
							/>
						</div>
					</div>
					<div className={cj('flex', isMobile && 'w-full justify-between')}>
						<div className="flex flex-col gap-1 p-2">
							<span className="block typo-body-xs text-text-secondary">
								Last Rate
							</span>
							<span className="block typo-heading-h5 text-text-default">
								{(() => {
									const rate = filteredFundingRates[0]?.fundingRate ?? 0;
									const precision = Math.abs(rate) > 0.01 ? 2 : 4;
									return parseFloat(rate.toFixed(precision));
								})()}
								%
							</span>
						</div>
						<div className="flex flex-col gap-1 p-2 pr-0">
							<span className="block typo-body-xs text-text-secondary max-sm:text-right">
								Avg Rate
							</span>
							<span className="block typo-heading-h5 text-text-default">
								{(() => {
									const precision = Math.abs(averageFundingRate) > 0.01 ? 2 : 4;
									return parseFloat(averageFundingRate.toFixed(precision));
								})()}
								%
							</span>
						</div>
					</div>
				</div>
				<div className="hidden-scroll min-h-[400px] grow overflow-auto p-0">
					{fundingRatesLoading ? (
						<div className="flex min-h-[400px] items-center justify-center">
							<InlineLoadingBar />
						</div>
					) : (
						<FundingPageChart
							page={'funding'}
							marketIndex={fundingMarketIndex}
							fundingRateData={filteredFundingRates}
							fundingInterval={fundingInterval}
							fundingPeriod={fundingPeriod}
						/>
					)}
				</div>
				<div className="flex items-center justify-between max-sm:flex-col max-sm:pt-md">
					<div className="flex items-center max-sm:pb-md">
						<div className="flex flex-row items-center">
							<div
								className="relative z-10 h-3 w-3 rounded-full"
								style={{
									backgroundColor: 'var(--color-text-disabled)',
								}}
							/>
						</div>
						<span className="mr-3 ml-1 inline-flex typo-body-xs text-text-secondary">
							Price
						</span>
						<div className="flex flex-row items-center">
							<div
								className="relative z-10 h-3 w-3 rounded-full"
								style={{
									backgroundColor: 'rgba(255,136,127,1)',
								}}
							/>
							<div
								className="z-20 -ml-[20%] h-3 w-3 rounded-full"
								style={{
									backgroundColor: 'rgba(67,194,96,1)',
								}}
							/>
						</div>
						<span className="ml-0.5 inline-flex typo-body-xs text-text-secondary">
							Funding rates
						</span>
					</div>

					<div className="gap-x-xs typo-body-xs text-text-secondary max-sm:border-t max-sm:border-stroke-divider max-sm:pt-md max-sm:text-center sm:inline-flex sm:items-center">
						<span className="mr-1">
							Funding payments may be skipped due to unhealthy oracles.
						</span>
						<Link href="https://docs.drift.trade/trading/oracles" openInNewTab>
							Learn more
						</Link>
					</div>
				</div>
			</GridSectionWrapper>

			<div className="hidden-scroll min-h-[400px] grow overflow-auto rounded-md p-0">
				<FundingTable fundingRates={fundingRates} />
			</div>
		</div>
	);
};

export default FundingHistoryContent;
